import { create } from "zustand";
import { log } from "../../logger";
import { usePreferencesStore, type ThemeMode, type Language } from "./preferences";
import { useScanHistoryStore } from "./scanHistory";
import type { SearchMode } from "../../features/search/searchUrl";
import { ConflictError, NotFoundError, UnreachableError, requests } from "../api";
import type { CatalogList, OwnCatalogEntry, SharedCatalogEntry } from "../api/requests";
import type {
  BulkMoveContainerResult,
  BulkMoveContainersInput,
  Container,
  ContainerInput,
  ContainerItem,
  FileLocation,
  Item,
  MoveResult,
  SearchResult,
  SessionIdentity,
} from "@egalregal/shared";
import { runSearch } from "../../features/search/searchRouter";
import { isPureDiscrete, parseQuery } from "../../features/search/queryParser";
import {
  cachedCatalogs,
  clearCatalogCache,
  initIndex,
  persistCatalogs,
  readCachedContainers,
} from "../../features/search/catalogStore";
import { setSearchLanguage } from "../../features/search/textFallback";
import { containerEntryLabel } from "../../features/catalogs/entryLabel";

export type { ThemeMode, Language };
export type AuthStatus = "unknown" | "anonymous" | "authenticated";

/**
 * Outcome of {@link AppState.openContainer}: `missing` means the server reports
 * the container unreadable (deleted, revoked or foreign); `unreachable` means
 * the lookup itself failed, so the caller keeps its current state.
 */
export type OpenContainerResult = "opened" | "missing" | "unreachable";

/**
 * Outcome of {@link AppState.resolveScannedContainer}: `not-found` means the
 * server answered that the caller may not read the container anywhere;
 * `unreachable` means no server answered (a transport failure), so the scanner
 * must not report it as unknown (scan-resolution 01). Any other failure (a 403,
 * a 500, an invalid payload) is rethrown and never treated as either.
 */
export type ScannedContainerResult =
  | { kind: "found"; container: Container }
  | { kind: "not-found" }
  | { kind: "unreachable" };

/** A media file staged in the form, uploaded once the container is saved (ticket 11). */
export interface PendingUpload {
  file: File;
  kind: "photo" | "audio";
  /** Camera-dialog capture location (ux-polish 03); travels with the upload. */
  location?: FileLocation | null;
}

/**
 * What saveContainer should write. The router owns which container is being
 * edited and which label a create carries, so both ids arrive explicitly and
 * no store field decides them (shell-redesign 07).
 */
export interface SaveContainerOptions {
  /** Update this container instead of creating one. */
  editingId?: string;
  /** Version of the container being edited; required together with editingId. */
  version?: number;
  /** Media staged in the form, uploaded after the container is written. */
  pending?: PendingUpload[];
  /** Catalog entry to create in; omitted keeps the selected-entry derivation. */
  entryId?: string;
  /** Scanned pre-printed label that becomes the new container's id. */
  createId?: string;
}

interface AppState {
  mode: ThemeMode;
  language: Language;
  online: boolean;
  auth: AuthStatus;
  authError?: string;
  /** Server-wide read-only demo (ADR-0045); read from the public auth config. */
  demoMode: boolean;
  /** The authenticated identity (ticket 07); set from /api/me and login. */
  user?: SessionIdentity;
  /** Own catalog entries (ticket 07). */
  entries: OwnCatalogEntry[];
  /** Entries other owners shared with me (ticket 07). */
  sharedWithMe: SharedCatalogEntry[];
  /** The currently browsed catalog entry; own default entry on login. */
  selectedEntryId?: string;
  containers: Container[];
  /**
   * The readable container union — every own and shared entry's members, from
   * `GET /api/containers` without `entry=` (catalog-entry-sharing 12). Browse
   * and detail keep using the entry-scoped {@link containers}; search, the
   * index and appearance search read this union.
   */
  catalogContainers: Container[];
  /**
   * Ids of containers created optimistically this session but not yet echoed
   * by the server. loadContainers only re-appends these, never every unknown
   * local container, so a container that authoritatively left the fetched
   * entry (e.g. after a catalog-entry move) is not resurrected (ticket 11).
   */
  optimisticCreateIds: string[];
  /**
   * Read-only mirror of the open container page (`/browse/:containerId`), for
   * background actions that already need the open container (shell-redesign
   * 07). The route decides the screen; this field never does.
   */
  selectedId?: string;
  searchResults: SearchResult[];
  /** True while a free-text search is awaiting the local router (ticket 06). */
  searchLoading: boolean;
  /**
   * Model-load progress as a 0..1 fraction, or null while loading without
   * byte progress (warm store read / session init). Written by the SearchView's
   * `embeddingService.onProgress` subscription; null renders indeterminate.
   */
  searchProgress: number | null;
  trash: Container[];
  /**
   * Containers opened this session, newest first — the empty-query recents on
   * `/search` (shell-redesign 09, spec §5). Session-only: recents are not a
   * persisted preference.
   */
  recentContainerIds: string[];
  /**
   * Quick-add current location (ticket 12): newly created containers link
   * under this container. Transient by decision — never written to `box.json`
   * (ADR-0003) and never persisted to localStorage (ADR-0030), so a reload or
   * another household member on the same browser starts without it.
   */
  currentLocationId?: string;
  /** Session geo fix for photo capture (camera 01); in-memory only, never persisted. */
  sessionLocation: FileLocation | null;
  /** True while a geo request for the session fix is in flight. */
  sessionLocationPending: boolean;
  /** Set (or clear) the quick-add current location (ticket 12). */
  setCurrentLocation: (id?: string) => void;
  setMode: (mode: ThemeMode) => void;
  setLanguage: (language: Language) => void;
  setOnline: (online: boolean) => void;
  setAuth: (auth: AuthStatus) => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  loadContainers: () => Promise<void>;
  /**
   * Refresh the readable container union in the background (ticket 12). Browse
   * keeps its entry-scoped slice; the union feeds search, the semantic/keyword
   * index and appearance search. A failed fetch keeps the previous snapshot,
   * so offline search covers whatever was cached.
   */
  loadCatalogContainers: () => Promise<void>;
  loadCatalogs: () => Promise<void>;
  selectEntry: (id?: string) => Promise<void>;
  renameEntry: (id: string, name: string) => Promise<void>;
  setDefaultEntry: (id: string) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  createEntry: (name: string) => Promise<void>;
  /** Reassign a container to another own catalog entry (catalog-entry-assignment 04). */
  assignEntry: (containerId: string, entryId: string, opts?: { version?: number; confirmTransfer?: boolean }) => Promise<void>;
  loadTrash: () => Promise<void>;
  /** SSE-driven refresh (catalog-entry-sharing 06): honors optional entryIds. */
  onSSESync: (data?: { entryIds?: string[] }) => Promise<void>;
  /** Rebuild the local search index (embeddings + keyword) for the current catalog. */
  syncSearchIndex: (containers: Container[], options?: { embed?: boolean }) => Promise<void>;
  /** Refresh the item mirror (ticket 07): sync items+placements, rebuild the index. */
  refreshItemMirror: () => Promise<void>;
  /** Mirror the container open on `/browse/:containerId`; the route owns it. */
  setSelectedId: (id?: string) => void;
  /**
   * Open a container from a world-scoped surface (catalog-entry-sharing 11).
   * The loaded slice answers instantly (offline path, no selection write);
   * otherwise the read-enforced server lookup names the container's catalog
   * entry, which becomes the selected entry (persisted per user, ADR-0030) and
   * reloads before the container is selected. `missing` when the container is
   * unreadable anywhere, `unreachable` when the lookup failed.
   */
  openContainer: (id: string) => Promise<OpenContainerResult>;
  /**
   * Resolve a scanned container id (catalog-entry-sharing 10). The loaded slice
   * answers instantly (offline path); otherwise the read-enforced server lookup
   * resolves a container reachable via a shared entry. A definite 404
   * (`not-found`) and a failed request (`unreachable`) stay distinct, so the
   * scanner can tell an unknown label from an offline one (scan-resolution 01).
   */
  resolveScannedContainer: (id: string) => Promise<ScannedContainerResult>;
  /**
   * Create or update a container. The caller names the container to edit and
   * the scanned label to create with (shell-redesign 07); no store field
   * decides either. An explicit `entryId` targets that catalog entry on create
   * (ticket 04); when omitted the own-active-entry derivation is kept so
   * existing callers behave unchanged.
   */
  saveContainer: (input: ContainerInput, options?: SaveContainerOptions) => Promise<Container>;
  removeContainer: (id: string, version: number, confirmChildren?: boolean) => Promise<void>;
  linkChild: (parentId: string, childId: string) => Promise<void>;
  unlink: (id: string, version: number) => Promise<void>;
  move: (
    id: string,
    parentId: string | null,
    version: number,
    entryId?: string,
    confirmTransfer?: boolean,
  ) => Promise<MoveResult>;
  /**
   * Move a batch of child containers to one destination entry (bulk-move 05,
   * ADR-0040). Reloads the catalogs and the entry-scoped containers but keeps
   * the source view and selection: the caller decides how to report and retain
   * the outcome. A cross-home batch transfers every row as a root and needs
   * `confirmTransfer`.
   */
  bulkMove: (
    containers: BulkMoveContainersInput["containers"],
    entryId: string,
    parentId: string | null,
    confirmTransfer?: boolean,
  ) => Promise<BulkMoveContainerResult>;
  search: (query: string, mode?: SearchMode) => Promise<void>;
  restore: (id: string, version: number) => Promise<void>;
  purge: (ids: string[]) => Promise<void>;
  removeFile: (containerId: string, fileId: string, version: number) => Promise<void>;
  /** Upload one media file to an existing container (ticket 20). */
  addFile: (
    containerId: string,
    file: File,
    kind: "photo" | "audio",
    location?: { latitude: number; longitude: number; capturedAt: string } | null,
  ) => Promise<void>;
  /**
   * Fetch (or refresh) the session geo fix the camera attaches to its shots
   * (camera 01). At most one request is in flight; a failure keeps the
   * previous fix, so a stale one is reused until a fresh one lands.
   */
  requestSessionLocation: () => void;
}

/**
 * The quick-add target (ticket 12) is only valid while its container is still
 * readable in the selected entry. Every invalidation site — identity change,
 * entry change, deletion, refresh — drops it through this one patch, so no
 * site can forget the field.
 *
 * @param state the current store state.
 * @param keep true when the target is still valid and must survive.
 * @returns a patch clearing `currentLocationId`, or an empty patch.
 */
function resetCurrentLocation(state: AppState, keep = false): Partial<AppState> {
  return keep || state.currentLocationId === undefined ? {} : { currentLocationId: undefined };
}

/**
 * Hydrate the persisted catalog list into the store when it is empty — the
 * cold-start half of the offline read cache (offline read cache 01). A missing
 * or corrupt record is a no-op, so the empty-catalog UI is unchanged.
 *
 * @param set the store's state setter.
 * @param get the store's state getter.
 */
async function hydrateCachedCatalogs(
  set: (partial: Partial<AppState> | ((state: AppState) => Partial<AppState> | AppState)) => void,
  get: () => AppState,
): Promise<void> {
  if (typeof indexedDB === "undefined" || get().entries.length > 0) return;
  const cached = await cachedCatalogs();
  if (!cached) return;
  set((state) =>
    state.entries.length > 0 ? state : { entries: cached.entries, sharedWithMe: cached.sharedWithMe },
  );
}

export const useAppStore = create<AppState>((set, get) => ({
  mode: usePreferencesStore.getState().mode,
  language: usePreferencesStore.getState().language,
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  auth: "unknown",
  authError: undefined,
  demoMode: false,
  user: undefined,
  entries: [],
  sharedWithMe: [],
  selectedEntryId: undefined,
  containers: [],
  catalogContainers: [],
  optimisticCreateIds: [],
  selectedId: undefined,
  searchResults: [],
  searchLoading: false,
  searchProgress: null,
  trash: [],
  recentContainerIds: [],
  currentLocationId: undefined,
  sessionLocation: null,
  sessionLocationPending: false,
  setCurrentLocation: (id) => set({ currentLocationId: id }),
  // Preferences stay the source of truth (ADR-0030); the app mirror keeps
  // existing selectors working until callers read the preferences store.
  setMode: (mode) => {
    usePreferencesStore.getState().setMode(mode);
    set({ mode });
  },
  setLanguage: (language) => {
    usePreferencesStore.getState().setLanguage(language);
    set({ language });
  },
  setOnline: (online) => set({ online }),
  setAuth: (auth) => set({ auth }),
  login: async (username, password) => {
    try {
      const identity = await requests.session.login(username, password);
      const previousSub = get().user?.sub;
      if (previousSub !== identity.sub) {
        // Switching users drops the previous user's in-memory selection;
        // loadCatalogs restores the new user's stored entry. Theme, language
        // and the per-user map stay in the preferences store.
        log.debug(
          { previousSub: shortId(previousSub), nextSub: shortId(identity.sub) },
          "[catalog] User changed on login; clearing in-memory selection",
        );
        // The cached union belongs to the previous identity: never rehydrate
        // it for the new user, offline or before the first network refresh.
        await clearCatalogCache();
        set((state) => ({
          selectedEntryId: undefined,
          containers: [],
          catalogContainers: [],
          optimisticCreateIds: [],
          entries: [],
          sharedWithMe: [],
          trash: [],
          selectedId: undefined,
          recentContainerIds: [],
          // The previous identity's quick-add target is not ours to keep.
          ...resetCurrentLocation(state),
        }));
      }
      set({ auth: "authenticated", user: identity, authError: undefined });
    } catch (err) {
      set({ authError: err instanceof Error ? err.message : "Login failed" });
    }
  },
  logout: async () => {
    // The signing-out user's scan-history bucket dies with the session; other
    // household members keep theirs (ADR-0041).
    const username = get().user?.sub;
    await requests.session.logout();
    // Anonymous sessions must not inherit the signed-out user's union either.
    await clearCatalogCache();
    useScanHistoryStore.getState().clearForUser(username);
    set((state) => ({
      auth: "anonymous",
      authError: undefined,
      user: undefined,
      entries: [],
      sharedWithMe: [],
      selectedEntryId: undefined,
      containers: [],
      catalogContainers: [],
      optimisticCreateIds: [],
      trash: [],
      recentContainerIds: [],
      selectedId: undefined,
      ...resetCurrentLocation(state),
    }));
  },
  checkAuth: async () => {
    try {
      const [identity, config] = await Promise.all([
        requests.session.me(),
        requests.session.authConfig(),
      ]);
      // Global search language (ADR-0036 decision 6) is served with the public
      // auth discovery; applied before the first keyword index build so the
      // browser stems with the server's language.
      if (config) setSearchLanguage(config.searchLanguage);
      const previousSub = get().user?.sub;
      const nextSub = identity?.sub;
      if (previousSub !== nextSub) {
        // Session identity changed (login, logout, or user switch outside
        // login()): drop the stale in-memory selection but keep the stored
        // per-user map plus theme and language.
        log.debug(
          { previousSub: shortId(previousSub), nextSub: shortId(nextSub) },
          "[catalog] Session identity changed; clearing in-memory selection",
        );
        // Same rule as login: the persisted union is identity-scoped.
        await clearCatalogCache();
        set((state) => ({
          selectedEntryId: undefined,
          containers: [],
          catalogContainers: [],
          optimisticCreateIds: [],
          entries: [],
          sharedWithMe: [],
          trash: [],
          selectedId: undefined,
          recentContainerIds: [],
          // The previous identity's quick-add target is not ours to keep.
          ...resetCurrentLocation(state),
        }));
      }
      set({
        auth: identity ? "authenticated" : "anonymous",
        user: identity ?? undefined,
        authError: undefined,
        demoMode: config?.demoMode ?? false,
      });
    } catch {
      set({ auth: "anonymous", authError: undefined, demoMode: false });
    }
  },
  loadContainers: async () => {
    const fetched = await requests.containers.list(get().selectedEntryId);
    const { containers: local, optimisticCreateIds } = get();
    const pending = new Set(optimisticCreateIds);
    const serverById = new Map(fetched.map((c) => [c.id, c]));
    // Merge: keep optimistic containers whose version is ahead of the server,
    // and replace the rest with the authoritative server state.
    const merged = fetched.map((c) => {
      const previous = local.find((lc) => lc.id === c.id);
      return previous && previous.version > c.version ? previous : c;
    });
    // Re-append only genuine optimistic creates the server has not echoed yet.
    // Appending every unknown local container is entry-blind and would
    // resurrect a container that authoritatively left this entry (ticket 11).
    for (const localContainer of local) {
      if (pending.has(localContainer.id) && !serverById.has(localContainer.id)) merged.push(localContainer);
    }
    set((state) => ({
      containers: merged,
      // The server has echoed these, so they are no longer optimistic.
      optimisticCreateIds: optimisticCreateIds.filter((id) => !serverById.has(id)),
      // A quick-add target that this entry no longer holds (deleted on another
      // device, moved away) must not survive as a broken link destination.
      ...resetCurrentLocation(state, merged.some((c) => c.id === state.currentLocationId)),
    }));
    // Search indexes the readable union, not this entry-scoped slice (ticket
    // 12): indexing `merged` here would prune every other entry's documents.
  },
  loadCatalogContainers: async () => {
    if (get().catalogContainers.length === 0 && typeof indexedDB !== "undefined") {
      // Hydrate the last persisted union first (ticket 12): search can answer
      // from an earlier session while the network leg is still in flight or
      // offline. The fetch below replaces it authoritatively.
      try {
        const cached = await readCachedContainers();
        if (cached.length > 0) {
          set((state) =>
            state.catalogContainers.length > 0 ? state : { catalogContainers: cached },
          );
        }
      } catch (err) {
        log.warn({ err }, "[catalog] Cached container union unavailable");
      }
    }
    // The persisted catalog entry list hydrates alongside the union (offline
    // read cache 01), so a boot that never reaches GET /api/catalogs still has
    // entry names, labels and writability. A live catalog load wins: this only
    // fills an empty list.
    await hydrateCachedCatalogs(set, get);
    try {
      const containers = await requests.containers.list();
      set({ catalogContainers: containers });
      // Rebuild incrementally in the background: unchanged documents keep
      // their vectors (hash/version diff), removed ones are pruned.
      await get().syncSearchIndex(containers);
    } catch (err) {
      // Offline / transient failure: keep the previous union so search can
      // still answer from what an earlier session cached (ticket 12).
      log.warn({ err }, "[catalog] Readable container union load failed; keeping the previous snapshot");
    }
  },
  syncSearchIndex: async (containers, options = {}) => {
    // A background catalog refresh must never wake the embedding model: the
    // search view opts in explicitly when the URL selects semantic mode
    // (shell-redesign 09, spec §5).
    const embed = options.embed ?? false;
    // An empty union is authoritative — every grant was revoked or every entry
    // deleted — so it must prune the cached containers and vectors instead of
    // returning early (catalog-entry-sharing review). Keep mirroring the
    // accessible item universe alongside the catalog (ticket 07): a failed sync
    // leaves items/placements undefined so initIndex reuses the stored
    // mirror — search stays functional offline (ADR-0026; review fix: an empty
    // *item* snapshot must never wipe the mirror).
    let synced: { items: Item[]; placements: ContainerItem[] } | null = null;
    try {
      synced = await requests.items.sync();
    } catch (err) {
      log.warn({ err }, "[item-mirror] sync failed; using the stored mirror");
    }
    // Idempotent: embeds missing, prunes removed, rebuilds keyword index,
    // persists the catalog + mirror. Non-fatal — search falls back to
    // keyword-only (B4). The server-maintained `data.searchText` rides on the
    // catalog/items already fetched above; catalogStore picks it up (ADR-0036).
    try {
      await initIndex(containers, synced?.items, synced?.placements, { embed });
    } catch (err) {
      log.warn({ err }, "[semantic-search] index build failed; falling back to keyword-only");
    }
  },
  refreshItemMirror: async () => {
    // Placement edits are not a search: keep the model asleep (spec §5).
    await get().syncSearchIndex(readableContainers(get()), { embed: false });
  },
  loadTrash: async () => {
    const trash = await requests.containers.trash();
    set({ trash });
  },
  // SSE events may name the entries they touch via entryIds (catalog-entry-
  // sharing 05). When they do, skip the container refetch unless the active
  // entry is affected; catalogs (shared metadata) always refresh. Events
  // without entryIds keep the legacy blind debounced refetch (05' fallback).
  onSSESync: async (data) => {
    const { selectedEntryId } = get();
    const entryIds = data?.entryIds;
    if (!entryIds || entryIds.length === 0) {
      // Legacy events carry no entry scope: refresh the readable union too
      // (ticket 12), since it spans every entry.
      void get().loadCatalogContainers();
      await Promise.all([get().loadContainers(), get().loadTrash()]);
      return;
    }
    if (selectedEntryId === undefined || entryIds.includes(selectedEntryId)) {
      await get().loadContainers();
    }
    // loadCatalogs refreshes the readable union and its index in the
    // background (ticket 12); the union spans every entry, so it always
    // refreshes regardless of which entry the event names.
    await get().loadCatalogs();
  },
  // --- Catalog entries (ticket 07) ------------------------------------------
  loadCatalogs: async () => {
    // Rehydration runs before the stored selection is read (ADR-0030): without
    // this a reload races localStorage and falls back to the own default even
    // though the user last browsed another entry. Re-reading is idempotent —
    // preference writes persist synchronously — so the catalog load always sees
    // the latest stored map.
    await usePreferencesStore.persist.rehydrate();
    let fetched: CatalogList;
    try {
      fetched = await requests.catalogs.list();
    } catch (err) {
      // Offline / transient failure: keep the in-memory list and, on a cold
      // start, hydrate the persisted one so entry names, labels and
      // writability survive (offline read cache 01). Only a successful load
      // replaces the cache record, so the store is left untouched here.
      log.warn({ err }, "[catalog] Catalog entry list load failed; keeping the previous snapshot");
      await hydrateCachedCatalogs(set, get);
      throw err;
    }
    const { selectedEntryId, user } = get();
    set({ entries: fetched.entries, sharedWithMe: fetched.sharedWithMe });
    // Write through so the next boot can answer without the API (offline read
    // cache 01). Best-effort: persistCatalogs logs and never throws.
    await persistCatalogs(fetched);
    // Catalog changes can add/remove readable entries (shares, deletion), so
    // the readable union and its index refresh alongside them (ticket 12).
    // Background: the entry-scoped browse load below stays unblocked.
    void get().loadCatalogContainers();
    const stored = usePreferencesStore.getState().selectedEntryIdFor(user?.sub);
    const resolved = resolveSelectedEntry(fetched.entries, fetched.sharedWithMe, stored);
    if (resolved !== selectedEntryId) {
      // Entry changed under us (stored entry deleted/unshared, or restored
      // after login/reload): drop the previous entry's members — the
      // optimistic append in loadContainers is entry-blind and would
      // otherwise bleed them into the new entry's tree.
      log.debug(
        { stored: shortId(stored), resolved: shortId(resolved), previous: shortId(selectedEntryId) },
        "[catalog] Resolved selected entry; clearing stale containers",
      );
      set((state) => ({
        selectedEntryId: resolved,
        containers: [],
        optimisticCreateIds: [],
        selectedId: undefined,
        // A quick-add target belongs to the entry it was picked in; the new
        // entry's tree must not inherit a foreign parent.
        ...resetCurrentLocation(state),
      }));
    }
    if (user?.sub && stored !== resolved) {
      // Heal a stale stored id (deleted/unshared) so the next reload lands
      // directly on the fallback.
      usePreferencesStore.getState().setSelectedEntryId(user.sub, resolved);
    }
  },
  selectEntry: async (id) => {
    const previous = get().selectedEntryId;
    set((state) => ({
      selectedEntryId: id,
      selectedId: undefined,
      // Switching entries: never carry the previous entry's members into the
      // new tree (the loadContainers optimistic append is entry-blind).
      containers: id === previous ? get().containers : [],
      // Dropping the members also drops their optimistic-create markers — the
      // new entry's fetch is authoritative for what it contains.
      optimisticCreateIds: id === previous ? get().optimisticCreateIds : [],
      // Quick-add is scoped to the entry it was picked in (ticket 12).
      ...resetCurrentLocation(state, id === previous),
    }));
    const userSub = get().user?.sub;
    if (userSub && id !== previous) {
      log.debug({ userSub, entryId: shortId(id) }, "[catalog] Entry selected; remembering it for the user");
      usePreferencesStore.getState().setSelectedEntryId(userSub, id);
    } else if (userSub && id !== undefined) {
      const stored = usePreferencesStore.getState().selectedEntryIdFor(userSub);
      if (stored !== id) usePreferencesStore.getState().setSelectedEntryId(userSub, id);
    }
    await get().loadContainers();
  },
  renameEntry: async (id, name) => {
    await requests.catalogs.patch(id, { name });
    await get().loadCatalogs();
  },
  setDefaultEntry: async (id) => {
    await requests.catalogs.patch(id, { isDefault: true });
    await get().loadCatalogs();
  },
  createEntry: async (name) => {
    const created = await requests.catalogs.create(name);
    await get().loadCatalogs();
    await get().selectEntry(created.id);
  },
  deleteEntry: async (id) => {
    await requests.catalogs.delete(id);
    await get().loadCatalogs();
    await get().loadTrash();
    await get().loadContainers();
  },
  assignEntry: async (containerId, entryId, opts) => {
    await requests.catalogs.assignContainer(entryId, containerId, opts);
    await Promise.all([get().loadCatalogs(), get().loadContainers()]);
  },
  setSelectedId: (id) =>
    set((state) => ({
      selectedId: id,
      // Remember only real opens (the detail's cleanup passes undefined).
      recentContainerIds: id
        ? [id, ...state.recentContainerIds.filter((containerId) => containerId !== id)].slice(0, 8)
        : state.recentContainerIds,
    })),
  openContainer: async (id) => {
    // Fast path: the entry-scoped slice already holds it — no round-trip, no
    // selection write, so offline opens keep working exactly as before.
    if (get().containers.some((c) => c.id === id)) {
      get().setSelectedId(id);
      return "opened";
    }
    const selectedEntryId = get().selectedEntryId;
    let resolved;
    try {
      resolved = await requests.containers.entry(id, selectedEntryId);
    } catch (err) {
      if (err instanceof NotFoundError) {
        log.debug({ containerId: shortId(id) }, "[catalog] Container not readable; nothing to open");
        return "missing";
      }
      log.warn({ err, containerId: shortId(id) }, "[catalog] Container entry lookup failed");
      return "unreachable";
    }
    const entryId = resolved.entry.id;
    try {
      if (entryId !== selectedEntryId) {
        // A newly shared entry may not be in the local lists yet: refresh them
        // first so the switcher and the breadcrumb root know the entry before
        // selectEntry persists it.
        const { entries, sharedWithMe } = get();
        if (!isKnownEntry(entries, sharedWithMe, entryId)) {
          await get().loadCatalogs().catch((err: unknown) => {
            // Scoping still works without the list; the breadcrumb falls back
            // to the top-level label until the next catalog load.
            log.warn({ err, entryId: shortId(entryId) }, "[catalog] Catalog refresh failed; scoping anyway");
          });
        }
        log.debug(
          { containerId: shortId(id), entryId: shortId(entryId), previous: shortId(selectedEntryId) },
          "[catalog] Container belongs to another entry; scoping to it",
        );
        await get().selectEntry(entryId);
      } else {
        // Same entry but the slice missed it (stale/offline slice): reload.
        await get().loadContainers();
      }
    } catch (err) {
      log.warn({ err, containerId: shortId(id), entryId: shortId(entryId) }, "[catalog] Scoping the container's entry failed");
      return "unreachable";
    }
    get().setSelectedId(id);
    return "opened";
  },
  resolveScannedContainer: async (id) => {
    const local = get().containers.find((c) => c.id === id);
    if (local) return { kind: "found", container: local };
    try {
      // Not merged into the entry-scoped slice: a foreign container would
      // render with the wrong breadcrumb root and Catalog dropdown. Opening it
      // goes through openContainer, which scopes the entry first (ticket 11).
      return { kind: "found", container: await requests.containers.get(id) };
    } catch (err) {
      if (err instanceof NotFoundError) {
        log.debug({ containerId: shortId(id) }, "[scan] Scanned container not readable by the caller");
        return { kind: "not-found" };
      }
      // Only a transport failure means "offline": a 403/500 from a reachable
      // server must reach the caller's own lookup-error path instead of being
      // reported as an uncached label.
      if (err instanceof UnreachableError) {
        log.warn({ err, containerId: shortId(id) }, "[scan] No answer from the server for a container lookup");
        return { kind: "unreachable" };
      }
      log.warn({ err, containerId: shortId(id) }, "[scan] Container lookup failed unexpectedly");
      throw err;
    }
  },
  saveContainer: async (input, options = {}) => {
    const { editingId, version, pending = [], entryId, createId } = options;
    // An explicit target from the create form wins (ticket 04). Otherwise keep
    // the legacy selected-entry derivation, extended by ticket 05 so a *writable
    // shared* selection is a valid create target too (own entries plus shared
    // entries at write or above; read-only selections fall back to no target).
    let activeEntryId = entryId;
    if (activeEntryId === undefined) {
      const { selectedEntryId, entries, sharedWithMe } = get();
      const own = entries.some((e) => e.id === selectedEntryId);
      const sharedWritable = sharedWithMe.some(
        (s) => s.entry.id === selectedEntryId && (s.level === "write" || s.level === "admin"),
      );
      activeEntryId = own || sharedWritable ? selectedEntryId : undefined;
    }
    let latest: Container;
    if (editingId && version !== undefined) {
      latest = await requests.containers.update(editingId, input, version);
      for (const upload of pending) {
        latest = await requests.media.upload(
          latest.id,
          upload.file,
          upload.kind,
          latest.version,
          upload.location,
        );
      }
    } else if (pending.length > 0) {
      latest = await requests.containers.createWithFiles(input, pending, createId, {
        entryId: activeEntryId,
      });
    } else {
      latest = await requests.containers.create(input, createId, { entryId: activeEntryId });
    }
    set((state) => {
      const others = state.containers.filter((c) => c.id !== latest.id);
      // A fresh create is staged locally before the server echoes it; mark it
      // so a loadContainers racing the echo preserves it (ticket 11).
      const isCreate = !(editingId && version !== undefined);
      return {
        containers: [latest, ...others],
        optimisticCreateIds:
          isCreate && !state.optimisticCreateIds.includes(latest.id)
            ? [...state.optimisticCreateIds, latest.id]
            : state.optimisticCreateIds,
      };
    });
    // The new/changed container belongs in the readable union too (ticket 12).
    void get().loadCatalogContainers();
    return latest;
  },
  removeContainer: async (id, version, confirmChildren = false) => {
    await requests.containers.delete(id, version, confirmChildren);
    set((state) => ({
      containers: state.containers.filter((c) => c.id !== id),
      selectedId: undefined,
      // Deleting the quick-add target stops auto-linking instead of pointing
      // the next create at a trashed container.
      ...resetCurrentLocation(state, state.currentLocationId !== id),
    }));
    await get().loadTrash();
    void get().loadCatalogContainers();
  },
  linkChild: async (parentId, childId) => {
    await requests.containers.linkChild(parentId, childId);
    await get().loadContainers();
    void get().loadCatalogContainers();
  },
  unlink: async (id, version) => {
    await requests.containers.unlinkFromParent(id, version);
    await get().loadContainers();
    void get().loadCatalogContainers();
  },
  move: async (id, parentId, version, entryId, confirmTransfer) => {
    const result = await requests.containers.move(id, parentId, version, entryId, confirmTransfer);
    // The server owns the container now; drop any optimistic-create marker so
    // a reload of the source entry cannot resurrect it (ticket 11).
    set((state) => ({ optimisticCreateIds: state.optimisticCreateIds.filter((cid) => cid !== id) }));
    if (entryId !== undefined && entryId !== get().selectedEntryId) {
      // Catalog-entry move: switch to the destination with the same clearing
      // discipline as selectEntry, refresh entry metadata, then re-open the
      // moved container on its new entry (ticket 11).
      await get().selectEntry(entryId);
      await get().loadCatalogs();
      set({ selectedId: id });
    } else {
      // Tree move within one entry: the entry membership is unchanged.
      await get().loadContainers();
    }
    void get().loadCatalogContainers();
    return result;
  },
  bulkMove: async (rows, entryId, parentId, confirmTransfer) => {
    const result = await requests.containers.bulkMove({
      containers: rows,
      entryId,
      parentId,
      confirmTransfer,
    });
    // The server owns the moved rows now; drop any optimistic-create marker so
    // a reload of the source entry cannot resurrect one (ticket 11).
    const movedIds = new Set(result.moved.map((container) => container.id));
    set((state) => ({
      optimisticCreateIds: state.optimisticCreateIds.filter((id) => !movedIds.has(id)),
    }));
    // Stay on the source entry (spec: post-move focus): refresh entry metadata
    // and the source slice instead of following the rows. loadCatalogs may
    // resolve a different entry, so the scoped reload follows it.
    await get().loadCatalogs();
    await get().loadContainers();
    void get().loadCatalogContainers();
    return result;
  },
  search: async (query, mode = "keyword") => {
    const trimmed = query.trim();
    if (!trimmed) {
      set({ searchResults: [], searchLoading: false, searchProgress: null });
      return;
    }
    // Search is catalog-agnostic (ADR-0023, ticket 12): rank over the readable
    // union when it has loaded, falling back to the entry slice while offline
    // before the first union load. The caller passes the URL's mode; keyword is
    // the default and no persisted preference exists (shell-redesign 09).
    const pool = readableContainers(get());
    const deps = { entryLabelOf: entryLabelResolver(get(), pool), mode };
    // Pure-discrete lookup never touches the model (C3) — resolve immediately
    // with no loading bar (ticket 06).
    if (isPureDiscrete(parseQuery(trimmed))) {
      // Local semantic/keyword search over the catalog (ADR-0022).
      const results = await runSearch(pool, trimmed, 20, deps);
      set({ searchResults: results, searchLoading: false, searchProgress: null });
      return;
    }
    // Free-text path: show the gallery-style loading indicator while the model
    // loads or ranks — semantic only (ADR-0022 B5). Keyword mode builds no
    // vectors and never wakes the model, so it resolves with no bar. The bar is
    // indeterminate until byte progress arrives (warm store read / session init
    // never fakes determinate progress). Always settles — a failure keeps the
    // previous results instead of hanging on the bar.
    const semantic = mode !== "keyword";
    if (semantic) set({ searchLoading: true, searchProgress: null });
    try {
      // Local semantic/keyword search over the union (ADR-0022, ticket 12).
      const results = await runSearch(pool, trimmed, 20, deps);
      set({ searchResults: results });
    } catch (err) {
      log.warn({ err }, "[semantic-search] search failed; keeping previous results");
    } finally {
      if (semantic) set({ searchLoading: false, searchProgress: null });
    }
  },
  restore: async (id, version) => {
    await requests.containers.restore(id, version);
    await Promise.all([get().loadContainers(), get().loadTrash()]);
    void get().loadCatalogContainers();
  },
  purge: async (ids) => {
    await requests.containers.purge(ids);
    await get().loadTrash();
    void get().loadCatalogContainers();
  },
  removeFile: async (containerId, fileId, version) => {
    const container = await requests.media.remove(containerId, fileId, version);
    set((state) => ({
      containers: state.containers.map((c) => (c.id === container.id ? container : c)),
    }));
    void get().loadCatalogContainers();
  },
  addFile: async (containerId, file, kind, location) => {
    const container = useAppStore.getState().containers.find((c) => c.id === containerId);
    if (!container) return;

    let retries = 0;
    let currentVersion = container.version;

    while (true) {
      try {
        const updated = await requests.media.upload(containerId, file, kind, currentVersion, location);
        set((state) => ({
          containers: state.containers.map((c) => (c.id === updated.id ? updated : c)),
        }));
        void get().loadCatalogContainers();
        return;
      } catch (err) {
        if (err instanceof ConflictError && retries < 3) {
          retries++;
          log.debug({ containerId, attempt: retries }, "[addFile] Version conflict, retrying with fresh container");
          const fresh = await requests.containers.get(containerId);
          currentVersion = fresh.version;
          continue;
        }
        throw err;
      }
    }
  },
  requestSessionLocation: () => {
    if (get().sessionLocationPending) return;
    const geo = typeof navigator === "undefined" ? undefined : navigator.geolocation;
    if (!geo?.getCurrentPosition) {
      log.debug("[camera] Geolocation unsupported; no session fix requested");
      return;
    }
    set({ sessionLocationPending: true });
    geo.getCurrentPosition(
      (pos) =>
        set({
          sessionLocation: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            capturedAt: new Date().toISOString(),
          },
          sessionLocationPending: false,
        }),
      (err) => {
        log.debug({ code: err.code }, "[camera] Geo fix unavailable; keeping the session fix");
        set({ sessionLocationPending: false });
      },
      { enableHighAccuracy: false, timeout: 5000 },
    );
  },
}));

/**
 * The container pool search and appearance search rank over (ticket 12): the
 * readable union once loaded, otherwise the selected entry's slice so offline
 * search still answers from what is resident.
 *
 * @param state the app store state (or a slice carrying both lists).
 */
export function readableContainers(state: {
  catalogContainers: Container[];
  containers: Container[];
}): Container[] {
  return state.catalogContainers.length > 0 ? state.catalogContainers : state.containers;
}

/**
 * Build the container→entry label resolver for search hits from the union's
 * root links (ticket 12). Search rows, gallery captions and the detail
 * breadcrumb therefore share `resolveEntryDisplayName`'s `{name}` /
 * `{name}@{owner}` form.
 */
function entryLabelResolver(
  state: Pick<AppState, "entries" | "sharedWithMe" | "selectedEntryId">,
  pool: Container[],
): (containerId: string) => string | null {
  return (containerId) =>
    containerEntryLabel(
      pool,
      state.entries,
      state.sharedWithMe,
      containerId,
      state.selectedEntryId,
    );
}

/**
 * Resolve which entry to browse (ADR-0030): stored per-user id wins when it
 * still exists, otherwise own default → first own → first shared → none.
 *
 * @param entries Own catalog entries from the server.
 * @param sharedWithMe Entries shared with the caller.
 * @param storedId The per-user stored selection, if any.
 * @returns The entry id to select, or undefined when no entry exists.
 */
function resolveSelectedEntry(
  entries: OwnCatalogEntry[],
  sharedWithMe: SharedCatalogEntry[],
  storedId?: string,
): string | undefined {
  if (storedId !== undefined) {
    const stillShared =
      entries.some((e) => e.id === storedId) || sharedWithMe.some((s) => s.entry.id === storedId);
    if (stillShared) return storedId;
  }
  return entries.find((e) => e.isDefault)?.id ?? entries[0]?.id ?? sharedWithMe[0]?.entry.id;
}

/**
 * Whether an entry id is already in the local catalog lists (own or shared).
 *
 * @param entries Own catalog entries from the server.
 * @param sharedWithMe Entries shared with the caller.
 * @param id The entry id to look up.
 */
function isKnownEntry(
  entries: readonly OwnCatalogEntry[],
  sharedWithMe: readonly SharedCatalogEntry[],
  id: string,
): boolean {
  return entries.some((e) => e.id === id) || sharedWithMe.some((s) => s.entry.id === id);
}

/**
 * Shorten long ids for log context (CODING_STANDARDS: UUIDs as abc1234).
 *
 * @param id The full id, if any.
 * @returns The first 7 characters, or undefined when absent.
 */
function shortId(id?: string): string | undefined {
  return id?.slice(0, 7);
}

// Keep the app mirror aligned when preferences change outside setMode /
// setLanguage (rehydration, direct preferences writes). Guarded so the
// dual-write above never loops.
usePreferencesStore.subscribe((prefs) => {
  const { mode, language } = useAppStore.getState();
  if (prefs.mode !== mode || prefs.language !== language) {
    useAppStore.setState({ mode: prefs.mode, language: prefs.language });
  }
});

// Point the scan-history store at the signed-in user's bucket. Login, logout,
// user switches and checkAuth all set `user`, so mirroring it here keeps the
// per-user history in step with the app's identity without a second source of
// truth (ADR-0041). The bucket itself is cleared only on logout, in logout().
useScanHistoryStore.getState().setActiveUser(useAppStore.getState().user?.sub);
useAppStore.subscribe((app) => {
  const username = app.user?.sub;
  if (useScanHistoryStore.getState().activeUser !== username) {
    useScanHistoryStore.getState().setActiveUser(username);
  }
});
